import React, {useEffect, useState} from 'react';
import '../stylesheets/SearchForm.css';
import {getHotelAvailability} from "../../utils/fetch/hotelFetching";
import {getLocal, setLocal, setSess} from "../../utils/dataFetching";
import {Hotel as HotelIcon, Place} from "@mui/icons-material";
import {formattedDate} from "../../utils/helpers";
import {useNavigate} from "react-router-dom";
import AutoGeoComplete from "../general/AutoGeoComplete";

const SearchForm = ({onSearchSubmit}) => {
  const parentKey = 'hotel';
  const navigate = useNavigate();
  const savedAirFormData = getLocal('airSearchFormData')
  const [formData, setFormData] = useState(getLocal(parentKey+'SearchFormData') || {
    destination: savedAirFormData ? savedAirFormData.destination[0].split(' ')[0] : '',
    checkInDate: savedAirFormData ? savedAirFormData.departDate[0] : '',
    checkOutDate: savedAirFormData ? savedAirFormData.returnDate : '',
    sortBy: 'DistanceFrom',
    radius: 10,
    pageSize: 10,
    rateMin: 0,
    rateMax: 9999,
    ratingMin: 3,
    ratingMax: 5
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [focusedInput, setFocusedInput] = useState('');
  const [inputSuggestValue, setInputSuggestValue] = useState('');

  const handleInputFocus = (e) => {
    setFocusedInput(e.target.name);
    e.stopPropagation();

    if (e.target.value?.length > 2 && e.target.value !== inputSuggestValue) {
      setInputSuggestValue(e.target.value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitted) {
      return;
    }
    setSubmitted(true);
    setError('');

    try {

      const searchResults = await getHotelAvailability(formData);

      onSearchSubmit(searchResults);
    } catch (error) {

      setError(error.message);
    }

    setSubmitted(false);
  };

  const handleToggleAdvanced = () => {
    setShowAdvanced(!showAdvanced);
  };

  // Save form data to localStorage on change
  useEffect(() => {
    setLocal(parentKey+'SearchFormData', formData);
  }, [formData]);

  const handleChange = (e) => {
    const v = e.target.value;
    const val = !isNaN(v) && v ? Number(v) : v;

    if (/destination/.test(e.target.name)) {
      if (e.currentTarget) {
        setInputSuggestValue(e.target.value);
      }
    }

    setFormData({
      ...formData,
      [e.target.name]: val
    });
  };

  const handleSkip = (e) => {
    e.stopPropagation();
    setSess('room','skipped');
    navigate('/next');
  }

  return (
    <div className="container search-form">
      <h2>
        <HotelIcon fontSize="large" /> Search <HotelIcon fontSize="large" />
      </h2>
      <button onClick={handleSkip} type="submit" className="m-3 btn btn-info skip">
        Skip
      </button>
      <form onSubmit={handleSubmit} className="row">
        <div className="form-group col-6">
          <div className="input-group">
              <span className="input-group-text"><Place fontSize="large" /></span>
              <input
                type="text"
                className="form-control"
                id="destination"
                name="destination"
                value={formData.destination}
                onChange={handleChange}
                placeholder="Enter destination"
                onFocus={handleInputFocus}
                autoComplete="off"
                required
              />
              <AutoGeoComplete name="destination"
                               value={formData.destination}
                               type={'CITY'}
                               handleChange={handleChange}
                               inputSuggestValue={inputSuggestValue}
                               focusedInput={focusedInput}
                               setFocusedInput={setFocusedInput} />
          </div>
        </div>
        <div className="form-group col-6">
          <label htmlFor="checkInDate">Check-in Date</label>
          <input
            type="date"
            className="form-control"
            id="checkInDate"
            name="checkInDate"
            value={formData.checkInDate}
            onChange={handleChange}
            min={formattedDate()}
            required
          />
        </div>
        <div className="form-group col-6">
          <label htmlFor="checkOutDate">Check-out Date</label>
          <input
            type="date"
            className="form-control"
            id="checkOutDate"
            name="checkOutDate"
            value={formData.checkOutDate}
            onChange={handleChange}
            min={formData.checkInDate}
            required
          />
        </div>

        <div className="form-group col-6 text-right">
          <button type="button" className={"me-5 btn btn-"+(showAdvanced?"warning":"dark")} onClick={handleToggleAdvanced}>
            Advanced
          </button>
          <button disabled={submitted ? true : false} 
            type="submit" 
            className={`ms-5 btn btn-${submitted ? 'secondary':'primary'}`}>
            {submitted ? 'Loading...' : 'Search'}
          </button>
        </div>

        {showAdvanced && (
          <>
            <div className="form-group col-4">
              <label htmlFor="sortBy">Sort By</label>
              <select
                className="form-control"
                id="sortBy"
                name="sortBy"
                value={formData.sortBy}
                onChange={handleChange}
              >
                <option value="DistanceFrom">Distance</option>
                <option value="TotalRate">Price</option>
                <option value="SabreRating">Rating</option>
              </select>
            </div>
            <div className="form-group col-4">
              <label htmlFor="radius">Distance From: </label>
              <input
                type="range"
                className="form-range"
                id="radius"
                name="radius"
                min={10}
                max={100}
                value={formData.radius}
                onChange={handleChange}
              />
              <span>{formData.radius} miles</span>
            </div>
            <div className="form-group col-4">
              <label htmlFor="pageSize">Page Size</label>
              <select
                className="form-control"
                id="pageSize"
                name="pageSize"
                value={formData.pageSize}
                onChange={handleChange}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="form-group col-4">
              <label htmlFor="rateMin">Price Min</label>
              <input
                type="number"
                className="form-control"
                id="rateMin"
                name="rateMin"
                value={formData.rateMin}
                onChange={handleChange}
              />
            </div>
            <div className="form-group col-4">
              <label htmlFor="rateMax">Price Max</label>
              <input
                type="number"
                className="form-control"
                id="rateMax"
                name="rateMax"
                value={formData.rateMax}
                onChange={handleChange}
              />
            </div>
            <div className="form-group col-4">
              <label htmlFor="ratingMin">Rating Min</label>
              <input
                type="number"
                className="form-control"
                id="ratingMin"
                name="ratingMin"
                min={1}
                max={5}
                step={0.5}
                value={formData.ratingMin}
                onChange={handleChange}
              />
            </div>
            <div className="form-group col-4">
              <label htmlFor="ratingMax">Rating Max</label>
              <input
                type="number"
                className="form-control"
                id="ratingMax"
                name="ratingMax"
                min={1}
                max={5}
                step={0.5}
                value={formData.ratingMax}
                onChange={handleChange}
              />
            </div>
          </>
        )}

      </form>
      {error && (<div className="form-group error">{error}</div> )}
    </div>
  );
};

export default SearchForm;
